import { Head } from "@inertiajs/react"
import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import { TiktokOAuthStartForm } from "@/components/social-accounts/tiktok-oauth-start-form"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import PublicLayout from "@/layouts/public-layout"
import { initI18n } from "@/lib/i18n/client"
import { resolvePublicLocale } from "@/lib/i18n-public"
import { signInPath, signupTiktokStartPath } from "@/routes"

// OKS-704 — `expired` + `completed` are distinct legible outcomes for a
// live-invite redemption that failed, replacing the blanket `unknown`
// dead-end. Neither is retryable: `completed` offers a creator-login CTA,
// `expired` a contact-support affordance (same shell as `unknown`).
export type SignupOauthReason =
  | "cancel"
  | "denied_scope"
  | "error"
  | "unknown"
  | "expired"
  | "completed"

export interface SignupOauthResultBrand {
  name: string | null
  logo_url: string | null
  locale: "en" | "fr"
}

export interface SignupOauthResultProps {
  reason: SignupOauthReason
  /** Signed invite token for the "Try again" re-auth form. Present only
   *  on retryable states (`cancel` / `denied_scope` / `error`) with a
   *  still-live invite; null otherwise (incl. every `unknown`). */
  retry_token: string | null
  brand: SignupOauthResultBrand
  /** `mailto:` to the brand/platform support address. */
  support_url: string
}

/**
 * Signups/OauthResult — OKS-466 TikTok OAuth result screen (UX Spec §B
 * recoverable / dead-end states).
 *
 * Renders one of four reasons. `cancel` / `denied_scope` / `error` are
 * recoverable: when a live invite token is echoed back, the "Try again"
 * CTA re-mints OAuth via a NATIVE form POST to `/signup/tiktok/start`
 * (cross-origin nav, never Inertia XHR). `unknown` is the disclosure-safe
 * "no crew account" dead-end — no retry, only a contact-support link.
 *
 * `role="alert"` on the message region so screen readers announce the
 * state on mount (denied-scope + error states, UX Spec §6 a11y).
 */
export default function SignupOauthResult({
  reason,
  retry_token,
  brand,
  support_url,
}: SignupOauthResultProps) {
  const { t } = useTranslation()
  const headingRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  // OKS-540 — re-init i18n with the resolved public locale on mount so the
  // client render matches the server's brand-derived locale (brand wins over
  // navigator; `?locale=`/`?lang=` still outrank). Mirrors InviteLanding.tsx.
  useEffect(() => {
    const locale = resolvePublicLocale({ brandLocale: brand.locale })
    initI18n({ locale })
  }, [brand.locale])

  const canRetry = retry_token != null
  // OKS-704 — an already-redeemed invite routes the creator to their
  // account login rather than a re-auth or support dead-end.
  const showLogin = reason === "completed"

  return (
    <PublicLayout>
      <Head>
        <title>{t("signups.oauth_result.head_title")}</title>
      </Head>
      <div
        data-testid="signup-oauth-result"
        data-reason={reason}
        className="mx-auto flex min-h-svh w-full max-w-md flex-1 flex-col items-center justify-center gap-6 px-6 py-10 text-center"
      >
        <Alert
          role="alert"
          className="text-left"
          data-testid="signup-oauth-result-alert"
        >
          <AlertTitle ref={headingRef} tabIndex={-1} className="outline-none">
            {t(`signups.oauth_result.${reason}.title`)}
          </AlertTitle>
          <AlertDescription>
            {t(`signups.oauth_result.${reason}.body`)}
          </AlertDescription>
        </Alert>

        {canRetry ? (
          <TiktokOAuthStartForm
            action={signupTiktokStartPath()}
            className="w-full"
            data-testid="signup-oauth-result-retry-form"
          >
            <input type="hidden" name="token" value={retry_token} />
            <Button
              type="submit"
              size="lg"
              className="w-full"
              data-testid="signup-oauth-result-retry"
            >
              {t("signups.oauth_result.retry_cta")}
            </Button>
          </TiktokOAuthStartForm>
        ) : showLogin ? (
          <Button
            asChild
            size="lg"
            className="w-full"
            data-testid="signup-oauth-result-login"
          >
            <a href={signInPath()}>{t("signups.oauth_result.login_cta")}</a>
          </Button>
        ) : (
          <Button
            asChild
            variant="outline"
            size="lg"
            className="w-full"
            data-testid="signup-oauth-result-support"
          >
            <a href={support_url}>{t("signups.oauth_result.support_cta")}</a>
          </Button>
        )}
      </div>
    </PublicLayout>
  )
}
